import React, { useEffect, useState } from "react";
import Main from "./Main";

import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";

import NoticeBoard from "../components/NoticeBoard";
import WalletCard from "../components/WalletCard";
import MiniStatCard from "../components/ui/MiniStatCard";
import ServiceTile from "../components/ui/ServiceTile";

import { useNavigate } from "react-router-dom";
import { useNotify } from "../context/NotifyContext";
import { useWallet } from "../context/walletContext";
import { formatAmount } from "../utils/formatAmount";
import { getServices } from "../api/admin/FrServicesApi";
import { REAL_BASE_URL } from "../api/axiosInstance";
import { fetchMyFinkedaAnalytics, fetchMyFinkedaOrders } from "../api/finkedaApi";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PerformanceInsights from "../components/PerformanceInsights";

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
};

const Dashboard = () => {
  const { walletAmt } = useWallet();
  const { notify } = useNotify();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [hasNotice, setHasNotice] = useState(true);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(true);

  const fetchServices = async () => {
    try {
      const res = await getServices();

      if (!res?.success) {
        notify(res?.message || "Failed to fetch services", "error");
        return;
      }

      setServices(res.data || []);
    } catch (error) {
      notify(
        error?.message || "Something went wrong while fetching services",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchServices();
    (async () => {
      const res = await fetchMyFinkedaOrders({
        page: 1,
        limit: 5,
        purpose: "PAYOUT",
      });
      if (res.success) {
        const items = res.data?.items || [];
        setPayoutSummary({
          total: res.data?.pagination?.total || items.length,
          lastStatus: items[0]?.status || null,
        });
      }
    })();
  }, []);

  const loadPerformance = async () => {
    setPerformanceLoading(true);
    const res = await fetchMyFinkedaAnalytics(dateRange);
    if (res.success) setPerformance(res.data);
    else notify(res.message || "Failed to load performance analytics", "error");
    setPerformanceLoading(false);
  };

  useEffect(() => {
    loadPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serviceIcon = (s) => {
    if (!s?.icon) return null;

    const path = String(s.icon).trim();

    if (!path || path === "null" || path === "undefined") {
      return null;
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return `${REAL_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const handleGoSettlement = () => {
  navigate("/money-settlement");
};

  return (
    <Main showRightPanel={false}>
      {/* ================= HEADER ================= */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2 lg:mb-4">
        <h1 className="hidden lg:block text-[20px] sm:text-[22px] font-semibold text-[var(--text-dark)]">
          Dashboard
        </h1>

        <div className="ds-panel flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 w-full sm:w-auto">
          <span className="text-[13px] font-medium text-[var(--text-muted)]">
            Today Amount:{" "}
            <span className="text-[15px] font-semibold text-[var(--text-dark)]">
              {/* ₹0.00 */}

              {`₹ ${formatAmount(walletAmt?.addMoney?.balance) ?? "0.00"}`}
            </span>
          </span>

          <button
  type="button"
  onClick={handleGoSettlement}
  className="ds-btn ds-btn-primary text-[12px] py-1.5 px-3"
>
  Settle Now
</button>
        </div>
      </div>

      {/* ================= DESKTOP ================= */}
      <div className="hidden lg:grid grid-cols-12 gap-4 items-stretch">
        {/* Notice */}
        {hasNotice && (
          <div className="col-span-4 min-w-0">
            <NoticeBoard onNoticeStatusChange={setHasNotice} />
          </div>
        )}

        {/* Main Wallet */}
        <div
          className={
            hasNotice
              ? "col-span-4 min-w-0"
              : "col-span-6 min-w-0"
          }
        >
          {/* <WalletCard /> */}
        </div>

        

        {/* Mini Stats */}
        <div
          className={
            hasNotice
              ? "col-span-4 flex flex-col gap-4 min-w-0"
              : "col-span-6 flex flex-col gap-4 min-w-0"
          }
        >
          <MiniStatCard
            className="flex-1"
            title="MATM Wallet Balance"
            value={`₹ ${formatAmount(walletAmt?.addMoney?.balance) ?? "0.00"}`}
            icon={<MonetizationOnOutlinedIcon fontSize="small" />}
          />

          <MiniStatCard
            className="flex-1"
            title="AEPS Wallet Balance"
            value={`₹ ${formatAmount(walletAmt?.aeps?.balance) ?? "0.00"}`}
            icon={<PercentOutlinedIcon fontSize="small" />}
          />
        </div>
      </div>

      {/* ================= MOBILE / TABLET ================= */}
      <div className="lg:hidden flex flex-col gap-2">
        <NoticeBoard onNoticeStatusChange={setHasNotice} />

        {/* <WalletCard />  */}

        <div className="grid grid-cols-2 gap-3">
          <MiniStatCard
            title="MATM Balance"
            value={`₹ ${formatAmount(walletAmt?.addMoney?.balance) ?? "0.00"}`}
            icon={<MonetizationOnOutlinedIcon fontSize="small" />}
          />

          <MiniStatCard
            title="AEPS Balance"
            value={`₹ ${formatAmount(walletAmt?.aeps?.balance) ?? "0.00"}`}
            icon={<PercentOutlinedIcon fontSize="small" />}
          />
        </div>
      </div>

      <div className="ds-card mt-2 lg:mt-4 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-soft make-text-primary">
              <PaymentsOutlinedIcon fontSize="small" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[var(--text-dark)] m-0">
                PG + Payout
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Collect card payment and send payout to a sender account in one checkout.
                {payoutSummary?.total
                  ? ` ${payoutSummary.total} payout order(s)`
                  : ""}
                {payoutSummary?.lastStatus
                  ? ` · last status ${payoutSummary.lastStatus}`
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="ds-btn ds-btn-primary"
              onClick={() => navigate("/pg-payout")}
            >
              Start PG + Payout
            </button>
            <button
              type="button"
              className="ds-btn ds-btn-secondary"
              onClick={() => navigate("/pg-payout/history")}
            >
              View reports
            </button>
          </div>
        </div>
      </div>

      {/* ================= SERVICES ================= */}
     <div className="ds-card mt-2 lg:mt-4 p-4 sm:p-5">
        <h2 className="text-[16px] font-semibold text-[var(--text-dark)] mb-4">
          Select Bill Category
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
          {services && services.length > 0 ? (
            services.map((s) => (
              <ServiceTile
                key={s._id}
                name={s.name}
                iconSrc={serviceIcon(s)}
                inactive={!s.isActive}
                onClick={() => {
                  if (s.isActive) {
                    navigate(`/service/${s.slug}`);
                  }
                }}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-[var(--text-muted)] text-sm">
              No services available
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 lg:mt-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
          <div>
            <h2 className="m-0 text-[16px] font-semibold text-[var(--text-dark)]">Your performance</h2>
            <p className="m-0 mt-1 text-[13px] text-[var(--text-muted)]">Personal transaction and service insights</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-[var(--text-muted)]">
              From
              <input
                type="date"
                className="ds-input mt-1 !w-auto"
                value={dateRange.startDate}
                max={dateRange.endDate}
                onChange={(event) => setDateRange((current) => ({ ...current, startDate: event.target.value }))}
              />
            </label>
            <label className="text-xs text-[var(--text-muted)]">
              To
              <input
                type="date"
                className="ds-input mt-1 !w-auto"
                value={dateRange.endDate}
                min={dateRange.startDate}
                onChange={(event) => setDateRange((current) => ({ ...current, endDate: event.target.value }))}
              />
            </label>
            <button type="button" className="ds-btn ds-btn-secondary" onClick={loadPerformance} disabled={performanceLoading}>
              Apply dates
            </button>
          </div>
        </div>
        <PerformanceInsights
          transactionInsight={performance?.transactionInsight}
          serviceInsight={performance?.serviceInsight}
          dailyTrend={performance?.dailyTrend}
          loading={performanceLoading}
        />
      </div>
    </Main>
  );
};

export default Dashboard;